import { useContext, useEffect, useState } from 'react';
import { GlobalContext } from '../../../context/GlobalContext';
import { unsafeAPI } from '../../../hooks/api';
import { Button } from '../../../shadcn/Button';
import { DateRangePicker } from '../../../shadcn/DateRangePicker';
import Notice from '../../../components/Notice';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import Input from '../../../components/Input';
import { Calendar, Play, Power, Save, Settings } from 'lucide-react';
import { API_BASE } from '../../../utils/constants';
import { DateRange } from 'react-day-picker';

const settlementDayOptions = [
	{ label: 'Lunes', value: '0' },
	{ label: 'Martes', value: '1' },
	{ label: 'Miércoles', value: '2' },
	{ label: 'Jueves', value: '3' },
	{ label: 'Viernes', value: '4' },
	{ label: 'Sábado', value: '5' },
	{ label: 'Domingo', value: '6' },
];

interface CronStatus {
	cron_enabled: boolean;
	auto_execute: boolean;
	country_filter: string | null;
	test_mode: boolean;
	settlement_days: number[] | null;
	last_toggled_by: string | null;
	last_toggled_at: string | null;
}

export default function SettlementControlPanel() {
	const { userData, internalCountry } = useContext(GlobalContext);

	const [cronStatus, setCronStatus] = useState<CronStatus | null>(null);
	const [loading, setLoading] = useState(true);
	const [showToggleConfirm, setShowToggleConfirm] = useState(false);
	const [notice, setNotice] = useState<{ message: string; variant: 'success' | 'error' | 'info' } | null>(null);

	// Settlement days
	const [settlementDays, setSettlementDays] = useState<number[]>([]);
	const [settlementDaysLoading, setSettlementDaysLoading] = useState(false);

	// Manual trigger state
	const [triggerDateRange, setTriggerDateRange] = useState<DateRange | undefined>(undefined);
	const [triggerLoading, setTriggerLoading] = useState(false);

	const fetchCronStatus = async () => {
		try {
			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/cron/status/`,
				{},
				userData,
				internalCountry?.get()
			);
			if (response?.success) {
				const days = response.settlement_days != null
					? Array.isArray(response.settlement_days) ? response.settlement_days : [response.settlement_days]
					: null;
				setCronStatus({
					cron_enabled: response.cron_enabled ?? false,
					auto_execute: response.auto_execute ?? false,
					country_filter: response.country_filter ?? null,
					test_mode: response.test_mode ?? false,
					settlement_days: days,
					last_toggled_by: response.last_toggled_by ?? null,
					last_toggled_at: response.last_toggled_at ?? null,
				});
				if (days != null) {
					setSettlementDays(days);
				}
			}
		} catch (err) {
			console.error('Error fetching cron status:', err);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchCronStatus();
	}, []);

	const handleToggleCron = async (): Promise<true | string> => {
		const newEnabled = !cronStatus?.cron_enabled;
		const body: { enabled: boolean; settlement_days?: number[] } = { enabled: newEnabled };
		if (settlementDays.length > 0) body.settlement_days = settlementDays;
		const response = await unsafeAPI(
			'POST',
			`${API_BASE}/payments/merchant_payouts/cron/toggle/`,
			body,
			userData,
			internalCountry?.get()
		);
		if (response?.success) {
			setCronStatus((prev) => (prev ? { ...prev, cron_enabled: newEnabled } : prev));
			setNotice({ message: `Cron ${newEnabled ? 'activado' : 'desactivado'} correctamente`, variant: 'success' });
			return true;
		}
		return response?.detail || 'Error al cambiar el estado del cron';
	};

	const handleSaveSettlementDays = async () => {
		if (settlementDays.length === 0) {
			setNotice({ message: 'Seleccione al menos un día de liquidación', variant: 'error' });
			return;
		}
		setSettlementDaysLoading(true);
		setNotice(null);
		try {
			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/cron/toggle/`,
				{ enabled: cronStatus?.cron_enabled ?? false, settlement_days: settlementDays },
				userData,
				internalCountry?.get()
			);
			if (response?.success) {
				setCronStatus((prev) => (prev ? { ...prev, settlement_days: settlementDays } : prev));
				setNotice({ message: 'Días de liquidación actualizados correctamente', variant: 'success' });
			} else {
				setNotice({ message: response?.detail || 'Error al actualizar días de liquidación', variant: 'error' });
			}
		} catch (err: any) {
			setNotice({ message: 'Error: ' + err.message, variant: 'error' });
		} finally {
			setSettlementDaysLoading(false);
		}
	};

	const handleTriggerSettlement = async () => {
		if (!triggerDateRange?.from || !triggerDateRange?.to) return;

		setTriggerLoading(true);
		setNotice(null);

		try {
			const response = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/trigger_settlement/`,
				{
					start_date: triggerDateRange.from.toISOString().split('T')[0],
					end_date: triggerDateRange.to.toISOString().split('T')[0],
					auto_execute: false,
					test_mode: false,
				},
				userData,
				internalCountry?.get()
			);

			if (response?.success) {
				setNotice({
					message: `Liquidaci\u00f3n iniciada. ID: ${response.run_id}`,
					variant: 'success',
				});
			} else {
				setNotice({
					message: response?.detail || 'Error al iniciar la liquidaci\u00f3n',
					variant: 'error',
				});
			}
		} catch (err: any) {
			setNotice({ message: 'Error: ' + err.message, variant: 'error' });
		} finally {
			setTriggerLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="animate-pulse space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/50 p-6">
				<div className="h-6 w-48 rounded bg-neutral-800" />
				<div className="h-10 w-full rounded bg-neutral-800" />
			</div>
		);
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-3 border-b border-neutral-800 pb-3">
				<div className="rounded-lg bg-amber-500/10 p-2">
					<Settings className="h-5 w-5 text-amber-500" />
				</div>
				<h3 className="text-lg font-semibold text-neutral-100">Control de Liquidaci&oacute;n</h3>
			</div>

			{showToggleConfirm && (
				<ConfirmDialog
					title={cronStatus?.cron_enabled ? 'Desactivar Cron' : 'Activar Cron'}
					message={
						cronStatus?.cron_enabled
							? '\u00bfEst\u00e1 seguro que desea desactivar el cron de pagos autom\u00e1ticos?'
							: '\u00bfEst\u00e1 seguro que desea activar el cron de pagos autom\u00e1ticos?'
					}
					onConfirm={handleToggleCron}
					onClose={() => setShowToggleConfirm(false)}
				/>
			)}

			{/* Cron Status */}
			<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
				<div className="flex items-center gap-3">
					<div
						className={`h-3 w-3 rounded-full ${cronStatus?.cron_enabled ? 'bg-emerald-500' : 'bg-neutral-600'}`}
					/>
					<span className="text-sm text-neutral-200">
						Cron autom&aacute;tico: <strong>{cronStatus?.cron_enabled ? 'Activo' : 'Inactivo'}</strong>
					</span>
					{cronStatus?.last_toggled_by && (
						<span className="text-xs text-neutral-500">
							(&Uacute;ltimo cambio por {cronStatus.last_toggled_by})
						</span>
					)}
				</div>
				<Button
					variant={cronStatus?.cron_enabled ? 'secondary' : 'primary'}
					className="w-fit"
					onClick={() => setShowToggleConfirm(true)}
				>
					<Power className="h-4 w-4" />
					{cronStatus?.cron_enabled ? 'Desactivar' : 'Activar'} Cron
				</Button>
			</div>

			{/* Settlement Days */}
			<div className="space-y-3">
				<div className="flex items-center gap-2">
					<Calendar className="size-4 text-neutral-400" />
					<span className="text-sm font-medium text-neutral-200">Días de liquidación</span>
				</div>
				<div className="flex flex-wrap gap-2">
					{settlementDayOptions.map((day) => {
						const dayNum = parseInt(day.value);
						const selected = settlementDays.includes(dayNum);
						return (
							<button
								key={day.value}
								type="button"
								onClick={() =>
									setSettlementDays((prev) =>
										selected ? prev.filter((d) => d !== dayNum) : [...prev, dayNum].sort()
									)
								}
								className={`rounded-lg border px-3 py-1.5 text-sm transition-colors ${
									selected
										? 'border-primary-500 bg-primary-500/15 text-primary-400'
										: 'border-neutral-700 bg-neutral-800/50 text-neutral-400 hover:border-neutral-600 hover:text-neutral-300'
								}`}
							>
								{day.label}
							</button>
						);
					})}
				</div>
				<div className="flex items-center">
					<Button
						variant="primary"
						className="w-fit whitespace-nowrap"
						loading={settlementDaysLoading}
						disabled={
							settlementDays.length === 0 ||
							(cronStatus?.settlement_days != null &&
								JSON.stringify(settlementDays) === JSON.stringify(cronStatus.settlement_days))
						}
						onClick={handleSaveSettlementDays}
					>
						<Save className="h-4 w-4" />
						Guardar Días
					</Button>
				</div>
			</div>

			{notice && <Notice message={notice.message} variant={notice.variant} />}

			{/* Manual Trigger */}
			<div className="space-y-4 rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
				<p className="text-sm font-medium text-neutral-300">Ejecutar Liquidaci&oacute;n Manual</p>
				<div className="flex flex-col gap-4 md:flex-row md:items-end">
					<DateRangePicker
						className="w-full md:w-auto md:min-w-[280px]"
						floatingLabel="Período"
						onRangeChange={(range) => setTriggerDateRange(range)}
					/>
					<Button
						variant="primary"
						className="w-fit whitespace-nowrap"
						loading={triggerLoading}
						disabled={!triggerDateRange?.from || !triggerDateRange?.to}
						onClick={handleTriggerSettlement}
					>
						<Play className="h-4 w-4" />
						Ejecutar Liquidaci&oacute;n
					</Button>
				</div>
			</div>
		</div>
	);
}
